import { Component, OnInit } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { EquityReviewService } from '../../equity-review/services/equity-review.service';
import { EquityReviewToCorrectiveActionPlanView } from '../../models/equity-review';

@Component({
  selector: 'app-equity-create-cap',
  templateUrl: './equity-create-cap.component.html',
  styleUrls: ['./equity-create-cap.component.scss'],
  standalone: false,
})
export class EquityCreateCapComponent implements OnInit {
  loading = true;
  activeCall = false;
  equityReviewId: string;
  systemicCitations: any[] = [];
  individualCitations: any[] = [];
  model: EquityReviewToCorrectiveActionPlanView;

  policyProcedureDataSource = new MatTableDataSource<any>([]);
  policyProcedureDisplayedColumns: string[] = ['federalRegulationCode', 'federalRegulationCodeShortDescription', 'push'];

  iepReviewDataSource = new MatTableDataSource<any>([]);
  iepReviewDisplayedColumns: string[] = [
    'federalRegulationCode',
    'federalRegulationCodeShortDescription',
    'countOfNonCompliant',
    'percentNonCompliant',
    'push',
  ];

  individualDataSource = new MatTableDataSource<any>([]);
  individualDisplayedColumns: string[] = ['name', 'dateOfBirth', 'stateAssignedId', 'iepStartDate', 'iepEndDate', 'push'];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly equityService: EquityReviewService
  ) {
    this.route.params.subscribe((params) => {
      this.equityReviewId = params['equityReviewId'];
    });
  }

  ngOnInit(): void {
    this.equityService.getEquityCorrectiveActionPlan(this.equityReviewId).subscribe((res) => {
      if (res.succeeded) {
        this.model = res.value;
        this.policyProcedureDataSource.data = this.model.systemicPolicyProcedureNonCompliances;
        this.iepReviewDataSource.data = this.model.systemicIepReviewNonCompliances;
        if (this.model.individualNonCompliances) {
          this.model.individualNonCompliances.forEach((c) => {
            c.learners?.forEach((l) => (l.selected = !!l.selected));
            this.individualDataSource[c.federalRegulationCodeId] = new MatTableDataSource<any>(c.learners || []);
          });
        }
      }
      this.loading = false;
    });
  }

  get anySelected(): boolean {
    if (!this.model) return false;
    return (
      [...this.policyProcedureDataSource.data, ...this.iepReviewDataSource.data]?.some((c) => c.selected) ||
      this.model?.individualNonCompliances?.some((c) => c.learners.some((l) => l.selected))
    );
  }

  toggle(item: any) {
    item.selected = !item.selected;
  }

  // Toggle a single learner
  toggleLearner(citation: any, learner: any) {
    learner.selected = !learner.selected;
    this.updateCitationSelectionState(citation);
  }

  // Header (Select All) checkbox
  toggleAllLearners(citation: any, event: MatCheckboxChange) {
    citation.learners?.forEach((l) => (l.selected = event.checked));
    this.updateCitationSelectionState(citation);
  }

  updateCitationAllSelected(citation: any) {
    this.updateCitationSelectionState(citation);
  }

  allSelected(citation) {
    return citation.learners.every((l) => l.selected);
  }

  private updateCitationSelectionState(citation: any) {
    const total = citation.learners?.length || 0;
    const selectedCount = citation.learners?.filter((l) => l.selected).length || 0;
    citation.allSelected = total > 0 && selectedCount === total;
    citation.partialSelected = selectedCount > 0 && selectedCount < total;
  }

  isCitationIndeterminate(citation: any): boolean {
    return !!citation.partialSelected;
  }

  create() {
    this.activeCall = true;
    const selectedSystemicPolicyProcedureNonCompliances = this.policyProcedureDataSource.data
      .filter((c) => c.selected)
      .map((c) => c.federalRegulationCodeId);
    const selectedSystemicIepReviewNonCompliances = this.iepReviewDataSource.data
      .filter((c) => c.selected)
      .map((c) => c.federalRegulationCodeId);
    const selectedIndividualNonCompliances = this.model.individualNonCompliances
      .map((c) => ({
        federalRegulationCodeId: c.federalRegulationCodeId,
        learnerIds: c.learners.filter((l) => l.selected).map((l) => l.id),
      }))
      .filter((c) => c.learnerIds.length > 0);

    const model = {
      equityReviewId: this.equityReviewId,
      systemicFederalRegulationCodeIds: [...selectedSystemicPolicyProcedureNonCompliances, ...selectedSystemicIepReviewNonCompliances],
      individualCitations: selectedIndividualNonCompliances,
    };

    this.equityService.createEquityCorrectiveActionPlan(this.equityReviewId, model).subscribe((res) => {
      if (res.succeeded) {
        window.opener?.postMessage(res.value, window.location.origin);
      }
      this.activeCall = false;
    });
  }
}
